import { FC, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { changeCustomer } from 'src/store/auth/reducer';
import { UserResponse } from 'src/types';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import {
  LOCAL_STORAGE_CURRENT_CUSTOMER_KEY,
  LOCAL_STORAGE_PROJECT_UUID_KEY,
} from 'src/utils/constants';

interface CompanySelectorProps {
  className?: string;
  customerLabel: string;
}

const CompanySelector: FC<CompanySelectorProps> = ({
 className = '',
 customerLabel,
}) => {
  const user: UserResponse = useSelector(
      (globalState: any) => globalState.auth.user
  );
  const projectRetainer = useSelector(
      (globalState: any) => globalState.projectRetainer
  );

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>(
      undefined
  );
  const [adminCustomers, setAdminCustomers] = useState<DefaultOptionType[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);

  const isAdmin = user?.contact?.type !== 'contact';

  // Fetch all customers for admin mode
  useEffect(() => {
    if (!isAdmin) return;

    const fetchAdminCustomers = async () => {
      try {
        setAdminLoading(true);
        const response = await apiRequests.get(apiRoutes.PROJECTS_LITE, {
          'filters[projects.status][]': 'active',
          'filters[projects.plan][]': ['starter', 'premium', 'enterprise', 'custom'],
          'page[size]': 500,
        });

        const customersMap = new Map<string, DefaultOptionType>();
        (response.data.data || []).forEach((project: any) => {
          if (project.customer && !customersMap.has(project.customer.uuid)) {
            customersMap.set(project.customer.uuid, {
              value: project.customer.uuid,
              label: project.customer.business_name,
            });
          }
        });

        setAdminCustomers(Array.from(customersMap.values()));
      } catch (error) {
        console.error('Error fetching admin customers:', error);
      } finally {
        setAdminLoading(false);
      }
    };

    fetchAdminCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const options = useMemo(() => {
    if (isAdmin) {
      return adminCustomers;
    }

    return (user?.projects as any[])
        ?.filter((item) => item.status === 'active')
        .map(
            (item): DefaultOptionType => ({
              value: item.customer.uuid,
              label: item.customer.business_name,
            })
        );
  }, [isAdmin, user?.projects, adminCustomers]);

  const fetchAndSaveCustomerObject = async (customerUuid: string) => {
    try {
      const response = await apiRequests.get(
          `${apiRoutes.CUSTOMERS}/${customerUuid}`
      );
      const customerObject = response.data.data;

      localStorage.setItem(
          LOCAL_STORAGE_CURRENT_CUSTOMER_KEY,
          JSON.stringify(customerObject)
      );
    } catch (error) {
      console.error('Error fetching and saving customer object:', error);
    }
  };

  const updateUserCompany = async (uuid: string) => {
    if (isAdmin) {
      // Admin mode: fetch project UUID for customer and save
      try {
        const response = await apiRequests.get(apiRoutes.PROJECTS, {
          'filters[customer.uuid][]': uuid,
          'page[size]': 1,
        });

        if (!response.data.data || response.data.data.length === 0) {
          console.error('No project found for customer', uuid);
          return;
        }

        const projectUuid = response.data.data[0].uuid;

        setSelectedCustomer(uuid);

        if (
            pathname.startsWith('/requests/') &&
            !pathname.startsWith('/requests/create')
        ) {
          navigate('/dashboard');
        }

        localStorage.setItem(LOCAL_STORAGE_PROJECT_UUID_KEY, projectUuid);
        await fetchAndSaveCustomerObject(uuid);

        window.location.reload();
      } catch (error) {
        console.error('Error updating company:', error);
      }
    } else {
      // Contact mode: use existing Redux flow
      setSelectedCustomer(uuid);
      const currentProject = user.projects.find(
          (project: any) => project.customer.uuid === uuid
      );

      if (!currentProject) return;

      if (
          pathname.startsWith('/requests/') &&
          !pathname.startsWith('/requests/create')
      ) {
        navigate('/dashboard');
      }

      dispatch(changeCustomer(uuid));
    }
  };

  // Set initial selected customer
  useEffect(() => {
    if (isAdmin) {
      // Admin mode: check if a project is scoped or auto-select first
      const savedProjectUuid = localStorage.getItem(
          LOCAL_STORAGE_PROJECT_UUID_KEY
      );

      if (savedProjectUuid && projectRetainer.data?.customer?.uuid) {
        setSelectedCustomer(projectRetainer.data.customer.uuid);
      } else if (adminCustomers.length > 0 && !selectedCustomer) {
        // Auto-select first customer if none selected
        updateUserCompany(adminCustomers[0].value as string);
      }
    } else {
      // Contact mode: existing logic
      if (!user?.projects?.length) return;

      const savedProjectUuid = localStorage.getItem(
          LOCAL_STORAGE_PROJECT_UUID_KEY
      );
      const validProject = user.projects.find(
          (p) => p.uuid === savedProjectUuid
      );
      const defaultProject = validProject || user.projects[0];
      const defaultCustomerUuid = defaultProject.customer.uuid;

      setSelectedCustomer(defaultCustomerUuid);

      if (!savedProjectUuid || !validProject) {
        localStorage.setItem(LOCAL_STORAGE_PROJECT_UUID_KEY, defaultProject.uuid);
      }

      const savedCustomerObject = localStorage.getItem(
          LOCAL_STORAGE_CURRENT_CUSTOMER_KEY
      );
      let shouldFetch = true;

      if (savedCustomerObject) {
        try {
          const customerObj = JSON.parse(savedCustomerObject);
          if (customerObj.uuid === defaultCustomerUuid) {
            shouldFetch = false;
          }
        } catch (e) {
          console.warn(
              'Error parsing client saved object. Searching again...',
              e
          );
        }
      }

      if (shouldFetch) {
        fetchAndSaveCustomerObject(defaultCustomerUuid);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.projects, isAdmin, adminCustomers, projectRetainer.data?.customer?.uuid]);

  // Hide selector if contact with only 1 project
  if (!isAdmin && user?.projects?.length === 1) {
    return null;
  }

  return (
      <Select
          size="large"
          className={`header__select !rounded-md ${className}`}
          value={selectedCustomer}
          loading={adminLoading}
          popupMatchSelectWidth={false}
          options={options}
          filterSort={(optionsA, optionsB) => {
            if (
                !optionsA.label ||
                !optionsB.label ||
                optionsA.label === optionsB.label
            ) {
              return 0;
            }

            return optionsA.label < optionsB.label ? -1 : 1;
          }}
          onChange={(e) => {
            updateUserCompany(e);
          }}
      />
  );
};

export default CompanySelector;
